import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/services/supabaseClient";
import type { OrganizationSettings } from "@/types/database";

type OrganizationSettingsUpdate = Partial<
  Omit<OrganizationSettings, "id" | "created_at" | "updated_at">
>;

async function fetchOrganizationSettings(): Promise<OrganizationSettings | null> {
  const { data, error } = await supabase
    .from("organization_settings")
    .select("*")
    .limit(1)
    .single();

  if (error && error.code !== "PGRST116") throw error;
  return data;
}

async function updateOrganizationSettings(
  updates: OrganizationSettingsUpdate,
): Promise<OrganizationSettings> {
  const existing = await fetchOrganizationSettings();

  if (existing) {
    const { data, error } = await supabase
      .from("organization_settings")
      .update(updates)
      .eq("id", existing.id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } else {
    const { data, error } = await supabase
      .from("organization_settings")
      .insert([{ organization_name: "Communauté des Béatitudes", ...updates }])
      .select()
      .single();

    if (error) throw error;
    return data;
  }
}

export function useOrganizationSettings() {
  return useQuery({
    queryKey: ["organization-settings"],
    queryFn: fetchOrganizationSettings,
  });
}

export function useUpdateOrganizationSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateOrganizationSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization-settings"] });
    },
  });
}
